import { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import api from '../services/api';

const QuickOptions = [
  { amount: 'INR 1,000', label: 'Provide basic medicine support for 1 child' },
  { amount: 'INR 5,000', label: 'Sponsor a full diagnostics blood screening panel' },
  { amount: 'INR 10,000', label: 'Fund doctor consultations at a rural health camp' },
  { amount: 'INR 25,000', label: 'Sponsor sub-specialist surgeries (e.g., Cataract)' }
];

function Donate() {
  const { settings } = useOutletContext();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    amount: '',
    donationType: 'One-time Donation',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Submitting your inquiry...');
    setSuccess(false);
    try {
      await api.post('/donation', form);
      setSuccess(true);
      setStatus('Thank you! Your donation inquiry has been received. Our coordinate team will contact you shortly.');
      setForm({ name: '', phone: '', email: '', amount: '', donationType: 'One-time Donation', message: '' });
    } catch (error) {
      console.error('Error submitting donation:', error);
      setStatus('Unable to submit inquiry. Please verify your connection or try again later.');
    }
  };

  return (
    <>
      {/* Page Hero */}
      <section className="page-hero" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.85), rgba(8, 50, 62, 0.85)), url(/images/hero_donate.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container">
          <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Charity & Trust</div>
          <h1>Support Our Mission</h1>
          <p>Your generosity directly funds healthcare access, medicines, and free screenings for those in need.</p>
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="sep">›</span>
            <span className="current">Donate Now</span>
          </div>
        </div>
      </section>

      {/* Main Donation Layout */}
      <section className="section-pad">
        <div className="container donate-grid">
          <div>
            <div className="eyebrow">How You Help</div>
            <h2>Make a Lasting Impact Today</h2>
            <p style={{ marginTop: 16, background: 'var(--teal-soft)', padding: '16px', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              {settings?.donationMessage || 'Your donation helps provide essential healthcare, life-saving medicines, and free medical screenings to individuals and families in need. Every contribution, no matter the size, brings hope, healing, and healthier futures to our community'}
            </p>
            
            <div style={{ marginTop: 32, display: 'grid', gap: 20 }}>
              {/* Bank Details */}
              <div style={{ background: 'var(--teal-soft)', borderRadius: 'var(--radius-lg)', padding: '28px', border: '1px solid var(--border)' }}>
                <h4 style={{ color: 'var(--teal-dark)', fontSize: '1.2rem', marginBottom: 12 }}>Bank Transfer Details</h4>
                <p style={{ margin: '4px 0', fontSize: '0.92rem', color: 'var(--teal-dark)', fontWeight: 'bold' }}>All donations are tax-exempted under section 80G of the Income Tax Act, 1961</p>
                <div style={{ marginTop: 12 }}>
                  <p style={{ margin: '4px 0', fontSize: '0.92rem' }}><strong>Account Name:</strong> {settings?.bankAccountName || 'Mega Hospital Foundation'}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.92rem' }}><strong>Account Type:</strong> Current Account</p>
                  <p style={{ margin: '4px 0', fontSize: '0.92rem' }}><strong>Account Number:</strong> {settings?.bankAccountNumber || '2051357683'}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.92rem' }}><strong>Bank Name:</strong> {settings?.bankName || 'Kotak Mahindra Bank'}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.92rem' }}><strong>IFSC Code:</strong> {settings?.bankIban || 'KKBK0005082'}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.92rem' }}><strong>Bank Branch:</strong> Prayagraj, Uttar Pradesh, INDIA</p>
                </div>
              </div>

              {/* Quick Options */}
              <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '28px', border: '1px solid var(--border)' }}>
                <h4 style={{ color: 'var(--teal-dark)', fontSize: '1.2rem', marginBottom: 16 }}>Donation Impact Packages</h4>
                <div style={{ display: 'grid', gap: 12 }}>
                  {QuickOptions.map(opt => (
                    <div key={opt.amount} style={{ display: 'flex', gap: 12, borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
                      <div style={{ fontWeight: 700, color: 'var(--gold-primary)', minWidth: 100 }}>{opt.amount}</div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--muted)' }}>{opt.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Donation Form Card */}
          <div className="card">
            <div className="eyebrow">Pledge Support</div>
            <h2 style={{ fontSize: '1.65rem' }}>Send Donation Inquiry</h2>
            <p style={{ fontSize: '0.88rem', margin: '8px 0 24px' }}>Fill out the details below to notify our accounts department of your contribution or to request details.</p>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Full Name" required />
              </div>
              
              <div className="form-group">
                <label>Phone Number</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number" />
              </div>
              
              <div className="form-group">
                <label>Email Address</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address" />
              </div>
              
              <div className="form-group">
                <label>Donation Option</label>
                <select name="amount" value={form.amount} onChange={handleChange}>
                  <option value="">Select or choose custom amount...</option>
                  <option value="INR 1,000">INR 1,000 (Basic Support)</option>
                  <option value="INR 5,000">INR 5,000 (Screening)</option>
                  <option value="INR 10,000">INR 10,000 (Health Camp)</option>
                  <option value="INR 25,000">INR 25,000 (Surgery Support)</option>
                  <option value="Custom Amount">Custom Amount / Equipment Support</option>
                </select>
              </div>

              <div className="form-group">
                <label>Donation Frequency</label>
                <select name="donationType" value={form.donationType} onChange={handleChange}>
                  <option value="One-time Donation">One-time Donation</option>
                  <option value="Monthly Recurring">Monthly Recurring</option>
                  <option value="In-kind / Medical Equipment">In-kind / Medical Equipment</option>
                  <option value="Corporate / CSR Partnership">Corporate / CSR Partnership</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message / Additional details</label>
                <textarea name="message" value={form.message} onChange={handleChange} placeholder="Describe your contribution, request details, or share instructions..."></textarea>
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}>Submit Inquiry</button>
              
              {status && (
                <p style={{ marginTop: 16, textAlign: 'center', fontSize: '0.9rem', color: success ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                  {status}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default Donate;
